/**
 * Chart palette.
 *
 * S-52-*inspired*, not S-52-compliant. The full IHO display specification is
 * hundreds of pages of symbology; we take the parts that carry safety meaning
 * and that mariners read without thinking:
 *
 *   - darker blue means shallower water (the S-52 convention; getting this
 *     backwards would be actively dangerous),
 *   - magenta means "danger" and nothing else ever uses it,
 *   - buff/tan is land, and a separate tan is drying land.
 *
 * Two variants are provided. Night is not a dimmed day palette — it is a
 * separate set chosen to preserve dark adaptation at the helm, which is why
 * whites become deep blue-greys rather than greys.
 */

export interface ChartPalette {
  /** Water deeper than the deep contour — no depth concern. */
  depthDeep: string;
  /** Between safety depth and the deep contour — passable, worth noticing. */
  depthCaution: string;
  /** Shallower than the safety depth for this boat — unsafe. */
  depthUnsafe: string;
  /** Dries at low water. */
  depthDrying: string;

  land: string;
  landEdge: string;
  builtUp: string;

  depthContour: string;
  safetyContourLine: string;

  soundingSafe: string;
  soundingUnsafe: string;

  /** Reserved exclusively for danger symbology. */
  danger: string;
  dangerHalo: string;

  buoyDefault: string;
  buoyRed: string;
  buoyGreen: string;
  buoyYellow: string;
  lightFlare: string;

  restrictedFill: string;
  restrictedLine: string;
  trafficLine: string;
  cableLine: string;

  routeLine: string;
  routeLineCasing: string;
  routeWaypoint: string;
  routeWarning: string;

  /**
   * Where the boat has been.
   *
   * Distinct from the route, which is where it is going — the two are on screen
   * together constantly and must never be read as one line. Orange, because
   * every colour S-52 does carry a meaning for is spoken for: blue is depth,
   * magenta is danger, tan is land, red and green are lateral marks.
   */
  track: string;

  vessel: string;
  vesselHalo: string;
  vesselAccuracy: string;

  /**
   * Webcam markers. Deliberately a colour outside S-52's vocabulary — a webcam
   * is an app annotation, not a charted object, and it should not be mistakable
   * for one.
   */
  webcam: string;
  webcamHalo: string;
  /** Harbour pins. Distinct from `webcam` so the two annotations do not merge. */
  harbour: string;
  harbourHalo: string;

  text: string;
  textHalo: string;
}

export const DAY_PALETTE: ChartPalette = {
  depthDeep: '#eef7fb',
  depthCaution: '#c3e2f0',
  depthUnsafe: '#7cc0dd',
  depthDrying: '#cbbd96',

  land: '#e8dcbc',
  landEdge: '#9c8f6d',
  builtUp: '#d8c9a4',

  depthContour: '#8fb8c9',
  safetyContourLine: '#2b7fa8',

  soundingSafe: '#4a6b7a',
  soundingUnsafe: '#1d4c63',

  danger: '#cc0099',
  dangerHalo: '#ffffff',

  buoyDefault: '#5a5a5a',
  buoyRed: '#d62c2c',
  buoyGreen: '#1f9d4d',
  buoyYellow: '#e8c020',
  lightFlare: '#e8b020',

  restrictedFill: 'rgba(204, 0, 153, 0.06)',
  restrictedLine: '#b0559a',
  trafficLine: '#9a7ab5',
  cableLine: '#7a9a5a',

  routeLine: '#0f4c81',
  routeLineCasing: '#ffffff',
  routeWaypoint: '#0f4c81',
  routeWarning: '#e03a1f',

  track: '#b25a00',

  vessel: '#c81e1e',
  vesselHalo: '#ffffff',
  vesselAccuracy: 'rgba(200, 30, 30, 0.12)',

  webcam: '#6b4bb8',
  webcamHalo: '#ffffff',
  harbour: '#0f7a5a',
  harbourHalo: '#ffffff',

  text: '#233238',
  textHalo: '#ffffff',
};

export const NIGHT_PALETTE: ChartPalette = {
  depthDeep: '#0a1a24',
  depthCaution: '#0e2836',
  depthUnsafe: '#16455c',
  depthDrying: '#3a3524',

  land: '#1c1a12',
  landEdge: '#4a4433',
  builtUp: '#262218',

  depthContour: '#2e5567',
  safetyContourLine: '#4d9ec4',

  soundingSafe: '#6a8a99',
  soundingUnsafe: '#9ac4dc',

  danger: '#ff3cc8',
  dangerHalo: '#1a0a16',

  buoyDefault: '#8a8a8a',
  buoyRed: '#c04040',
  buoyGreen: '#2f9d5d',
  buoyYellow: '#b09030',
  lightFlare: '#b08830',

  restrictedFill: 'rgba(255, 60, 200, 0.07)',
  restrictedLine: '#8a4478',
  trafficLine: '#6a5580',
  cableLine: '#5a7040',

  routeLine: '#5aa8e0',
  routeLineCasing: '#0a1a24',
  routeWaypoint: '#5aa8e0',
  routeWarning: '#ff6a4a',

  track: '#d8862a',

  vessel: '#ff4a4a',
  vesselHalo: '#0a1a24',
  vesselAccuracy: 'rgba(255, 74, 74, 0.12)',

  webcam: '#b39ae8',
  webcamHalo: '#0a1a24',
  harbour: '#3fbf95',
  harbourHalo: '#0a1a24',

  text: '#c8d8e0',
  textHalo: '#0a1a24',
};

export type PaletteName = 'day' | 'night';

export const PALETTES: Record<PaletteName, ChartPalette> = {
  day: DAY_PALETTE,
  night: NIGHT_PALETTE,
};
